import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Schemas } from '../types/api-types';
import apiClient from '../clients/apiClient';
import { getAllSystemMenus } from '../services/systemMenuService';
import { useAuth } from './AuthProvider';

export type UserPermission = Schemas['UserPermissionResponse'];
export type UserMenu = Schemas['UserMenuResponse'];

/** Strip the query string so "/transaction?setup=X" and "/transaction" compare equal. */
const menuPathOf = (toUrl?: string | null): string => (toUrl || '').split('?')[0];

interface UserPermissionAndMenuContextType {
    userPermissions: UserPermission[] | null;
    userMenus: UserMenu[] | null;
    userFavoriteMenus: UserMenu[] | null;
    systemMenus: UserMenu[] | null;
    loading: boolean;
    error: string | null;
    fetchUserPermissions: () => Promise<void>;
    fetchUserMenus: () => Promise<void>;
    fetchUserFavouriteMenus: () => Promise<void>;
    hasPermission: (permissionId: string) => boolean;
    hasMenu: (menuId: number) => boolean;
    getAccessibleRoutes: () => string[];
    /**
     * Whether the current user may open a route.
     * `ready` is false until grants have loaded (caller should wait, not deny).
     * Fails open when the path has no it_system_menu row at all - some menu rows
     * in this system are managed ad hoc in the DB, not in source, so an unknown
     * path is treated as "not menu-controlled" rather than forbidden.
     */
    canAccessMenuPath: (path: string) => { allowed: boolean; ready: boolean };
    /** First route the user can actually open, for redirecting "/". */
    firstAccessibleRoute: () => string | null;
    /** True once /users/menu_list has resolved (success or failure). */
    menusSettled: boolean;
    setRefreshTrigger: (refresh: boolean) => void;
}

const UserPermissionAndMenuContext = createContext<UserPermissionAndMenuContextType | undefined>(undefined);

export const UserPermissionAndMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [userPermissions, setUserPermissions] = useState<UserPermission[] | null>(null);
    const [userMenus, setUserMenus] = useState<UserMenu[] | null>(null);
    const [userFavoriteMenus, setUserFavoriteMenus] = useState<UserMenu[] | null>(null);
    const [systemMenus, setSystemMenus] = useState<UserMenu[] | null>(null);
    // True once the /users/menu_list fetch has finished, success OR failure. The
    // route guard / home redirect wait on this rather than on `userMenus !== null`
    // so a failing menu_list degrades to NoAccessPage (recoverable via Retry)
    // instead of an endless spinner.
    const [menusSettled, setMenusSettled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { isAuthenticated, loading: authLoading } = useAuth();
    const [refreshTrigger, setRefreshTrigger] = useState(false);

    const fetchUserPermissions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(`/users/permission_list`);
            setUserPermissions(response.data.data || []);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            const errorMessage = (typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : null)) || err?.message || 'Failed to fetch user permissions';
            setError(errorMessage);
            setUserPermissions(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchUserMenus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(`/users/menu_list`);
            setUserMenus(response.data.data || []);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            const errorMessage = (typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : null)) || err?.message || 'Failed to fetch user menus';
            setError(errorMessage);
            setUserMenus(null);
        } finally {
            setLoading(false);
            setMenusSettled(true);
        }
    }, []);

    const fetchUserFavouriteMenus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(`/users/favourite_menu_list`);
            setUserFavoriteMenus(response.data.data || []);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            const errorMessage = (typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : null)) || err?.message || 'Failed to fetch user menus';
            setError(errorMessage);
            setUserFavoriteMenus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchSystemMenus = useCallback(async () => {
        try {
            setSystemMenus((await getAllSystemMenus()) as UserMenu[]);
        } catch (err: any) {
            // Non-fatal: canAccessMenuPath falls open when the catalog is unknown.
            setSystemMenus(null);
        }
    }, []);

    const hasPermission = useCallback((permissionId: string): boolean => {
        if (!userPermissions) return false;
        return userPermissions.some(p => p.permission === permissionId);
    }, [userPermissions]);

    const hasMenu = useCallback((menuId: number): boolean => {
        if (!userMenus) return false;
        return userMenus.some(m => m.id === menuId && m.has_menu === true);
    }, [userMenus]);

    const getAccessibleRoutes = useCallback((): string[] => {
        if (!userMenus) return [];
        // Get all menu routes that have has_menu = true (are actually accessible routes)
        return userMenus
            .filter(m => m.has_menu === true && m.to_url)
            .map(m => m.to_url!)
            .filter((url, index, self) => self.indexOf(url) === index); // Remove duplicates
    }, [userMenus]);

    const canAccessMenuPath = useCallback((path: string): { allowed: boolean; ready: boolean } => {
        if (!menusSettled) return { allowed: false, ready: false };
        const target = menuPathOf(path);
        const granted = (userMenus || []).some(m => m.has_menu !== false && menuPathOf(m.to_url) === target);
        if (granted) return { allowed: true, ready: true };
        // Unknown to the menu catalog -> not menu-controlled -> allow. If menu_list
        // failed (userMenus null) we can't prove a grant, so a known path is denied
        // (NoAccessPage, recoverable via Retry) rather than left spinning.
        const known = (systemMenus || []).some(m => menuPathOf(m.to_url) === target);
        return { allowed: !known, ready: true };
    }, [menusSettled, userMenus, systemMenus]);

    const firstAccessibleRoute = useCallback((): string | null => {
        const first = (userMenus || [])
            .filter(m => m.has_menu === true && m.to_url && m.component)
            .sort((a, b) => (a.order_num ?? 0) - (b.order_num ?? 0))[0];
        return first?.to_url ?? null;
    }, [userMenus]);

    // Fetch permissions and menus when user changes
    useEffect(() => {
        if (authLoading || !isAuthenticated) {
            return;
        }
        fetchUserPermissions();
        fetchUserMenus();
        fetchUserFavouriteMenus();
        fetchSystemMenus();
    }, [authLoading, fetchUserPermissions, fetchUserMenus, fetchUserFavouriteMenus, fetchSystemMenus, isAuthenticated]);

    useEffect(() => {
        if (refreshTrigger) {
            setMenusSettled(false);
            fetchUserPermissions();
            fetchUserMenus();
            fetchUserFavouriteMenus();
            fetchSystemMenus();
            setRefreshTrigger(false);
        }
    }, [refreshTrigger]);

    return (
        <UserPermissionAndMenuContext.Provider
            value={{
                userPermissions,
                userMenus,
                userFavoriteMenus,
                systemMenus,
                loading,
                error,
                fetchUserPermissions,
                fetchUserMenus,
                fetchUserFavouriteMenus,
                hasPermission,
                hasMenu,
                getAccessibleRoutes,
                canAccessMenuPath,
                firstAccessibleRoute,
                menusSettled,
                setRefreshTrigger,
            }}
        >
            {children}
        </UserPermissionAndMenuContext.Provider>
    );
};

export const useUserPermissionAndMenu = (): UserPermissionAndMenuContextType => {
    const context = useContext(UserPermissionAndMenuContext);
    if (!context) {
        throw new Error('useUserPermissionAndMenu must be used within UserPermissionAndMenuProvider');
    }
    return context;
};
